﻿import {
    AirportDelay,
    AirportDelayType,
    DelayTrend,
    FlightConstrainedArea,
    FlightConstrainedAreaResponse,
    GetConstraintsResponse,
    RerouteAdvisory
} from '../components/constraints-list/types';
import constraintsApi from '../services/http/constraints-api';
import { DateTimeFormatter, UnitFormatter } from '../formatters';
import { Position } from 'geojson';

let rerouteAdvisories: RerouteAdvisory[] = [];
let airportDelays: AirportDelay[] = [];
let flightConstrainedAreas: FlightConstrainedArea[] = [];
let lastRerouteUpdate: Date | null = null;
let lastAirportDelayUpdate: Date | null = null;
let lastFlightConstrainedAreaUpdate: Date | null = null;
let callbacks: (() => void)[] = [];

function getRerouteAdvisories() {
    return rerouteAdvisories;
}

function getAirportDelays() {
    return airportDelays;
}

function getFlightConstrainedAreas() {
    return flightConstrainedAreas;
}

function getLastRerouteUpdate() {
    return lastRerouteUpdate;
}

function getLastAirportDelayUpdate() {
    return lastAirportDelayUpdate;
}

function getLastFlightConstrainedAreaUpdate() {
    return lastFlightConstrainedAreaUpdate;
}

function onUpdated(callback: () => void) {
    callbacks.push(callback);
}

function offUpdated(callback: () => void) {
    callbacks = callbacks.filter((cb) => cb !== callback);
}

function notifySubscribers() {
    for (const callback of callbacks) {
        callback();
    }
}

// API calls
async function fetchConstraints(): Promise<void> {
    const response = await constraintsApi.fetchConstraints();
    if (response && response.responseCode === 0) {
        processConstraintsData(response);
    }
}
function processConstraintsData(data: GetConstraintsResponse): void {
    rerouteAdvisories = data.reroutes.map((r) => ({
        advisoryNumber: r.advisoryNumber,
        type: r.bitmap & (1 << 6) ? 'Msg' : 'Adv',
        category: getRerouteCategoryText(r.advisoryCategory),
        action: getRerouteActionText(r.advisoryAction),
        status: r.bitmap & (1 << 2) ? 'Active' : 'Planned',
        method: r.bitmap & (1 << 4) && r.bitmap & (1 << 5) ? 'FCA' : r.bitmap & (1 << 4) ? 'ETA' : 'ETD',
        conformantFlights: 0, //todo
        nonConformantFlights: 0, //todo
        affectedRoutes: r.routes.length,
        reRouteName: r.rerouteName,
        routes: r.routeText.map((route, index) => ({
            distance: null,
            route: route,
            sequence: index + 1
        })),
        get details() {
            return r.detail;
        }
    }));
    airportDelays = data.delays.map((d) => ({
        name: d.airportID,
        fullName: d.airportName,
        type: getDelayTypeText(d.type as AirportDelayType),
        reason: d.reason,
        start: d.startTime ? new Date(d.startTime) : null,
        end: d.stopTime ? new Date(d.stopTime) : null,
        position: [d.airportLon, d.airportLat],
        affectedFlights: undefined,
        get details(): string {
            return formatDelayDetails(d, this);
        },
        get verboseDetails(): string {
            return formatVerboseDelayDetails(d, this);
        }
    }));
    flightConstrainedAreas = data.fcas.map((fca) => ({
        name: fca.label,
        type: `${fca.condition === 0 ? 'FCA' : 'FEA'}${getFcaTypeText(fca.type)}`,
        reason: fca.detail,
        start: fca.startTime ? new Date(fca.startTime) : null,
        end: fca.endTime ? new Date(fca.endTime) : null,
        altitude: fca.ceiling ? fca.ceiling * 100 : null,
        affectedFlights: undefined,
        shape: fca.shape,
        center: getCenterPosition(fca),
        radius: fca.radius,
        points: fca.fcApoints.map((p) => [p.lon, p.lat]),
        get details() {
            return [
                `Type:\t\t\t${this.type}`,
                `NAS name:\t\t${fca.nasName ? `${fca.nasName} (${getNasType(fca.shape)})` : `(${getNasType(fca.shape)})`}`,
                `Reason:\t\t\t${fca.detail}`,
                this.altitude ? `Ceiling:\t\t${UnitFormatter.formatAltitude(this.altitude)}` : null,
                fca.speed ? `Speed:\t\t\t${UnitFormatter.formatSpeed(fca.speed * 100)}` : null,
                fca.direction ? `Direction:\t\t${fca.direction} degrees` : null,
                fca.startTime ? `FCA start:\t\t${DateTimeFormatter.formatDateTime(this.start)}` : null,
                fca.endTime ? `FCA end:\t\t${DateTimeFormatter.formatDateTime(this.end)}` : null,
                fca.afpStartTime ? `AFP start:\t\t${DateTimeFormatter.formatDateTime(new Date(fca.afpStartTime))}` : null,
                fca.afpEndTime ? `AFP end:\t\t${DateTimeFormatter.formatDateTime(new Date(fca.afpEndTime))}` : null,
                fca.filter ? `\n${fca.filter}` : null
            ]
                .filter(Boolean)
                .join('\n');
        }
    }));

    lastRerouteUpdate = new Date(data.rerouteLastUpdate);
    lastAirportDelayUpdate = new Date(data.faaDelayLastUpdate);
    lastFlightConstrainedAreaUpdate = new Date(data.fcaLastupdte);

    notifySubscribers();
}

const getCenterPosition = (fca: FlightConstrainedAreaResponse): Position => {
    if (fca.centerLatitude && fca.centerLongitude) {
        return [fca.centerLongitude, fca.centerLatitude];
    }
    if (fca.fcApoints.length > 0) {
        const midIndex = Math.floor(fca.fcApoints.length / 2);
        return [fca.fcApoints[midIndex].lon, fca.fcApoints[midIndex].lat];
    }
    return [0, 0];
};

function formatDelayDetails(delayData: any, delay: AirportDelay): string {
    const type = delayData.type as AirportDelayType;
    let result = '';

    // Add delay type description
    result += getDelayTypeDescription(type) + '\n';

    // Add timing information
    if (shouldShowStartTime(type)) {
        result += `     Start time: ${DateTimeFormatter.formatDateTime(delay.start)}\n`;
    }

    if (shouldShowEndTime(type)) {
        const label = isClosureType(type) ? 'Reopen time' : 'End time';
        result += `     ${label}: ${DateTimeFormatter.formatDateTime(delay.end)}\n`;
    }

    // Add delay duration information
    if (hasDelayDuration(type)) {
        const label = type === AirportDelayType.GroundDelay ? 'Average delay' : 'Minimum delay';
        result += `     ${label}: ${delayData.averageOrMinDelay} minutes\n`;
    }

    if (hasMaxDelay(type)) {
        result += `     Maximum delay: ${delayData.maxDelay} minutes\n`;
    }

    // Add trend information
    if (hasTrend(type)) {
        const trendText = delayData.trend === DelayTrend.Increasing ? 'Increasing' : 'Decreasing';
        result += `     Trend: ${trendText}\n`;
    }

    // Add reason
    if (delay.reason && delay.reason.length > 0) {
        result += `     Reason: ${delay.reason}\n`;
    }

    return result.trim();
}

function formatVerboseDelayDetails(delayData: any, delay: AirportDelay): string {
    const type = delayData.type as AirportDelayType;
    const airportName = delay.fullName || delay.name;
    const trendText = delayData.trend === DelayTrend.Increasing ? 'increasing' : 'decreasing';

    switch (type) {
        case AirportDelayType.Arrival:
            return `Due to ${delay.reason}, arrivals into ${airportName} are experiencing airborne holding delays between ${delayData.averageOrMinDelay} and ${delayData.maxDelay} minutes and ${trendText}.`;

        case AirportDelayType.Departure:
            return `Due to ${delay.reason}, departures from ${airportName} are experiencing gate hold and taxi delays between ${delayData.averageOrMinDelay} and ${delayData.maxDelay} minutes and ${trendText}.`;

        case AirportDelayType.GroundDelay:
            return `Due to ${delay.reason}, there is a Traffic Management Program (Ground Delay) in effect for ${airportName}. This is causing some arriving flights to be delayed an average of ${delayData.averageOrMinDelay} minutes.`;

        case AirportDelayType.GroundStop:
            return `Due to ${delay.reason}, there is a Traffic Management Program (Ground Stop) in effect for traffic arriving ${airportName}. End time is ${DateTimeFormatter.formatDateTime(delay.end)}.`;

        case AirportDelayType.PartialClosure:
            return `Due to ${delay.reason}, ${airportName} has a partial closure at ${DateTimeFormatter.formatDateTime(delay.start)}. Reopen time is ${DateTimeFormatter.formatDateTime(delay.end)}.`;

        case AirportDelayType.Closure:
        default:
            return `Due to ${delay.reason}, ${airportName} has been closed at ${DateTimeFormatter.formatDateTime(delay.start)}. Reopen time is ${DateTimeFormatter.formatDateTime(delay.end)}.`;
    }
}

function getDelayTypeDescription(type: AirportDelayType): string {
    switch (type) {
        case AirportDelayType.Arrival:
            return 'Arrival delay';
        case AirportDelayType.Departure:
            return 'Departure delay';
        case AirportDelayType.GroundDelay:
            return 'Ground delay (Traffic Management Program)';
        case AirportDelayType.GroundStop:
            return 'Ground stop (Traffic Management Program)';
        case AirportDelayType.PartialClosure:
            return 'Airport partial closure';
        case AirportDelayType.Closure:
            return 'Airport closure';
        default:
            return '...';
    }
}

function shouldShowStartTime(type: AirportDelayType): boolean {
    return type === AirportDelayType.Closure || type === AirportDelayType.PartialClosure;
}

function shouldShowEndTime(type: AirportDelayType): boolean {
    return type === AirportDelayType.Closure || type === AirportDelayType.GroundStop || type === AirportDelayType.PartialClosure;
}

function isClosureType(type: AirportDelayType): boolean {
    return type === AirportDelayType.Closure || type === AirportDelayType.PartialClosure;
}

function hasDelayDuration(type: AirportDelayType): boolean {
    return type === AirportDelayType.Arrival || type === AirportDelayType.Departure || type === AirportDelayType.GroundDelay;
}

function hasMaxDelay(type: AirportDelayType): boolean {
    return type === AirportDelayType.Arrival || type === AirportDelayType.Departure;
}

function hasTrend(type: AirportDelayType): boolean {
    return type === AirportDelayType.Arrival || type === AirportDelayType.Departure;
}

function getDelayTypeText(typeInt: AirportDelayType): string {
    switch (typeInt) {
        case AirportDelayType.None:
            return 'None';
        case AirportDelayType.Arrival:
            return 'Arrival delay';
        case AirportDelayType.Departure:
            return 'Departure delay';
        case AirportDelayType.Closure:
            return 'Closure';
        case AirportDelayType.PartialClosure:
            return 'Partial closure';
        case AirportDelayType.GroundStop:
            return 'Ground stop';
        case AirportDelayType.GroundDelay:
            return 'Ground delay';
        default:
            return 'Unknown';
    }
}

function getFcaTypeText(typeInt: number): string {
    let result = '';

    if (typeInt & 0x01) {
        result += ' (AFP)';
    }
    if (typeInt & 0x02) {
        result += ' (FSM eligible)';
    }
    if (typeInt & 0x04) {
        result += ' (Extended FEA)';
    }
    return result;
}

function getNasType(shapeInt: number): string {
    switch (shapeInt) {
        case 4:
            return 'airport';
        case 5:
            return 'sector';
        case 6:
            return 'base sector';
        case 7:
            return 'TRACON';
        case 8:
            return 'base TRACON';
        case 9:
            return 'base ARTCC';
        case 10:
            return 'fix';
        case 11:
            return 'SUA';
        default:
            return 'unknown';
    }
}

function getRerouteCategoryText(categoryInt: number): string {
    switch (categoryInt) {
        case 1:
            return 'CDR';
        case 2:
            return 'FCA';
        case 3:
            return 'INFO';
        case 4:
            return 'MISC';
        case 5:
            return 'NAT';
        case 6:
            return 'NRP';
        case 7:
            return 'PLAYBOOK';
        case 8:
            return 'ROUTE';
        case 9:
            return 'SHUTTLE';
        case 10:
            return 'SPECOPS';
        case 11:
            return 'VACAPES';
        case 12:
            return 'FEA';
        default:
            return 'Unknown';
    }
}

function getRerouteActionText(actionInt: number): string {
    switch (actionInt) {
        case 1:
            return 'FYI';
        case 2:
            return 'PLN';
        case 3:
            return 'RMD';
        case 4:
            return 'RQD';
        default:
            return 'Unknown';
    }
}

async function fetchAll(): Promise<void> {
    try {
        await fetchConstraints();
        notifySubscribers();
    } catch (error) {
        console.error('Error fetching constraint data:', error);
    }
}

async function fetchConstraintsNow(): Promise<void> {
    await fetchAll();
}

export default {
    fetchConstraintsNow,
    getRerouteAdvisories,
    getAirportDelays,
    getFlightConstrainedAreas,
    getLastRerouteUpdate,
    getLastAirportDelayUpdate,
    getLastFlightConstrainedAreaUpdate,
    onUpdated,
    offUpdated
};
